import { defineStore } from "pinia";
import usuarioModel from "@/models/usuarioModel";
import { home } from "../utils/usuarioRedirections";
import garantiaModel from "@/models/garantiaModel";
import { alertError } from "../utils/alerts";
import {
  clearStoredSession,
  hasSessionToken,
  redirectToWebsiteLogin,
} from "@/utils/session";

export const useUsuarioStore = defineStore("usuario", {
  state: () => ({
    user: { ...usuarioModel.data },
    loginError: null,
    referencia_id: null,
    returnUrl : null,
    resumenGarantias: { 
      saldo: 0, 
      credito: 0,
      ofertado: 0 , 
      saldo_pendiente: 0,
      oferta_sin_garantia:false
    },
    errores : [],
    registerEndedSuccessfully: false,
    
  }),
  actions: {
    async iniciarSesion(
      email = this.user.email,
      password = this.user.password
    ) {
      try {
        this.loginError = null;
        let returnUrl = this.returnUrl ?? globalThis.location.origin + '/';
        const response = await usuarioModel.iniciarSesion(email, password, returnUrl);
        if (response && response.token) {
          localStorage.setItem("token", response.token);
          localStorage.setItem("user", JSON.stringify(response.user));
          console.log(response.data)
          if (response.redirect_url) {
                globalThis.location.href = response.redirect_url;
                return true;
            }
          return true;
        }
        this.loginError = "Respuesta inválida del servidor. Intentá nuevamente.";
        return false;
      } catch (error) {
        alertError(error.response?.data?.message || "Error en el inicio de sesión. Verificá tus credenciales e intentá nuevamente.");
        if (error.response?.status === 401) {
          localStorage.removeItem("token");
          localStorage.removeItem("user");

          this.loginError =
            error.response.data.error || "Error en la autenticación";
        } else if (error.response) {
          this.loginError = `Error ${error.response.status}: ${
            error.response.data?.error || error.response.data?.message || "sin detalle"
          }`;
        } else {
          this.loginError = "No se pudo conectar con el servidor.";
        }
        return false;
      }
    },

    async getSession() {
      if (!hasSessionToken()) {
        clearStoredSession();
        return false;
      }

      try {
        const response = await usuarioModel.getSession();
        localStorage.setItem("user", JSON.stringify(response.user.original));
        this.user = response.user.original;
        return true;
      } catch (error) {
        if (error.response?.status === 401) {
          clearStoredSession();
          this.user = { ...usuarioModel.data };
          redirectToWebsiteLogin();
        } else {
          console.error("No se pudo validar la sesión actual", error);
        }

        return false;
      }
    },

    async refrescarResumenGarantiasUsuario() {
      const token = localStorage.getItem("token");
      if (!token) return;

      try {
        const data = await garantiaModel.obtenerResumenGarantia();
        this.actualizarResumenGarantias(data);
      } catch (error) {
        console.log(error);
        this.resumenGarantias = { saldo: 0, credito: 0 };
        console.log("Error al obtener resumen");
      }
    },

    actualizarResumenGarantias(data) {
      if (!data) return;

      localStorage.setItem("credito", data.credito);
      localStorage.setItem("saldo", data.saldo);
      Object.assign(this.resumenGarantias, data);
    },

    async registrarUsuario(
      email = this.user.email,
      password = this.user.password,
      password_confirmation = this.user.password_confirmation,
      acepta_terminos = this.user.acepta_terminos,
      acepta_ddjj = this.user.acepta_ddjj,
      referencia_id = this.referencia_id
    ) {

      try {
        this.user.name = this.user.email.split("@")[0];
        let returnUrl = this.returnUrl ?? globalThis.location.origin + '/';
        await usuarioModel.registrarUsuario(this.user.name, email, password, password_confirmation, acepta_ddjj, acepta_terminos, referencia_id, returnUrl);
        return true
      } catch (error) {
        alertError(error.response.data.message);
        return false;
      }
    },

    async confirmarRegistro(pendingId, token) {
      this.errores = [];

      if (!pendingId || !token) {
        this.errores.push(
          "El enlace de confirmación es inválido o está incompleto. Solicitá uno nuevo."
        );
        return;
      }

      try {
        const res = await usuarioModel.confirmarRegistro(pendingId, token);

        if (res.data?.token) {
          localStorage.setItem("user", JSON.stringify(res.data.user));
          localStorage.setItem("token", res.data.token);
        }
        
        if (res.data?.redirect_url) {
              globalThis.location.href = res.data.redirect_url;
              return;
        }
        home();

      } catch (e) {
        const msg =
          e?.response?.data?.error ||
          e?.response?.data?.message ||
          e?.message ||
          "No se pudo confirmar el registro. Solicitá un nuevo correo de confirmación.";

        this.errores.push(msg);
      }
    },



    async cerrarSesion() {
      try {
        localStorage.setItem("user", null);
        await usuarioModel.cerrarSesion();
      } catch (e) {
        console.error(e);
      } finally {
        this.$reset();
        localStorage.removeItem("user");
        localStorage.removeItem("credito");
        localStorage.removeItem("saldo");
        localStorage.removeItem("token");
        globalThis.location.href = process.env.VUE_APP_SISTEMA_SUBASTAS_ACTUAL_URL + "/logout"
      }
    },

    async confirmarLoginExterno({ token, session_id, id }) {
      try {
        const response = await usuarioModel.confirmarLoginExterno({
          token,
          session_id,
          id,
        });

        localStorage.setItem("token", response.token);
        localStorage.setItem("user", JSON.stringify(response.user));

        this.token = response.token;
        this.user = response.user;
      } catch (error) {
        console.log(error);
      }
    },

    async getHistorialOfertas () {
      try {
        const result = await usuarioModel.obtenerHistorialOfertas();
        this.historialOfertas = result;        
      } catch (error) {
        console.error("Error al obtener historial de ofertas", error);
        alertError("No se pudo obtener el historial de ofertas. Intentá nuevamente más tarde.");
      }
    },

    async obtenerReferencias() {
      try {
        const referencias = await usuarioModel.obtenerReferencias();
        this.referencias = referencias;
      } catch (error) {
        console.error("Error al obtener referencias:", error);
      }
    },

    getReferencias() {
      return this.referencias || [];
    }
  },
  getters: {
    isLoggedIn() {
      return localStorage.getItem("token") === null ? false : true;
    },
    getUsuario() {
      return JSON.parse(localStorage.getItem("user")) ?? null;
    },
    getCreditoUsuario() {
      return Number(this.resumenGarantias.credito) || 0;
    },
    getOfertaSinGarantiaUsuario() {
      return Boolean(this.resumenGarantias.oferta_sin_garantia) || false;
    },
    getSaldoUsuario() {
      return Number(this.resumenGarantias.saldo) || 0;
    },
    isAuthenticated() {
      return localStorage.getItem("token") !== null;
    },  
  },
});
